import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import { uploadRosterJSON } from '@/app/services/students/students';
import * as Actions from '@/app/store/actions';
import { isInValidName } from '@/utils/utils';
import {
	CircularProgress,
	Dialog,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import XLSX from 'xlsx';
import { uploadSchedule } from '@/app/services/rooms/roomsschedules';
import { Error } from '@material-ui/icons';
import GradientButton from 'shared/components/GradientButton';

dayjs.extend(customParseFormat);

const ScheduleFileValidator = ({ file, type, rooms }) => {
	const dispatch = useDispatch();
	const [rows, setRows] = useState([]);
	const [errList, setErrList] = useState([]);
	const [isValidating, setIsValidating] = useState(true);
	const [errs, setErrs] = useState(false);
	const [open, setOpen] = useState(false);
	const [isAdding, setIsAdding] = useState(false);

	const handleProceedAnyway = () => {
		setOpen(true);
	};

	const convertTo24Hour = (timeStr) => {
		try {
			const parsed = dayjs(timeStr, 'h:mm A');
			if (!parsed.isValid()) return null;
			return parsed.format('HH:mm');
		} catch (error) {
			return null;
		}
	};

	const formatTimeForDisplay = (timeStr) => {
		try {
			const parsed = dayjs(timeStr, 'HH:mm');
			if (!parsed.isValid()) return timeStr;
			return parsed.format('h:mm A');
		} catch (error) {
			return timeStr;
		}
	};

	useEffect(() => {
		if (type === 'local') {
			const reader = new FileReader();
			reader.onload = (e) => {
				try {
					const data = e.target.result;
					const readedData = XLSX.read(data, { type: 'binary' });
					const wsname = readedData.SheetNames[0];
					const ws = readedData.Sheets[wsname];
					const dataParse = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
					let arrays = dataParse.filter((array) => array.length !== 0);

					// get the headers from the first row (they start at index 1 after "All columns are mandatory")
					const headers = arrays[0].slice(1); // skip "All columns are mandatory"
					// get the data rows (everything after the header row)
					const dataRows = arrays.slice(1);
					const validFormat = ['Room', 'Start Time', 'End Time', 'Subject', 'Activity Type'];

					// header validation
					if (!headers || headers.length < validFormat.length) {
						dispatch(
							Actions.showMessage({
								message: `Schedule format is invalid. Please use the format provided.`,
								variant: 'error',
								autoHideDuration: 3000,
							})
						);
						dispatch(Actions.closeDialog());
						return;
					}

					// verify headers match expected format
					const headerMismatches = validFormat.map((expected, index) => ({
						expected,
						found: headers[index],
						matches: headers[index] === expected,
					}));

					if (headerMismatches.some((h) => !h.matches)) {
						const mismatches = headerMismatches
							.filter((h) => !h.matches)
							.map((h) => `Expected "${h.expected}" but found "${h.found || 'nothing'}"`)
							.join(', ');

						dispatch(
							Actions.showMessage({
								message: `Invalid headers: ${mismatches}`,
								variant: 'error',
								autoHideDuration: 3000,
							})
						);
						dispatch(Actions.closeDialog());
						return;
					}

					// transform data rows to match expected format
					const transformedRows = dataRows.map((row) => ({
						room: row[1], // Room
						start_time: row[2], // Start Time
						end_time: row[3], // End Time
						subject: row[4], // Subject
						activity_type: row[5], // Activity Type
					}));

					setRows(transformedRows);

					// continue with validation
					const temp = [];
					for (let i = 0; i < transformedRows.length; i += 1) {
						temp[i] = [];
						const row = transformedRows[i];

						// validate room exists
						const matchingRoom = rooms.find((r) => r.name.toLowerCase() === row.room?.toLowerCase());
						if (!row.room || !matchingRoom) {
							temp[i][1] = 'Room not found in available rooms';
						} else {
							transformedRows[i].room_id = matchingRoom.id;
						}

						// validate and convert start time
						if (!row.start_time) {
							temp[i][2] = 'Start time is required';
						} else {
							const converted = convertTo24Hour(row.start_time);
							if (!converted) {
								temp[i][2] = 'Invalid start time format (use h:mm AM/PM, e.g., 9:30 AM)';
							} else {
								// store the converted time
								transformedRows[i].start_time = converted;
							}
						}

						// validate and convert end time
						if (!row.end_time) {
							temp[i][3] = 'End time is required';
						} else {
							const converted = convertTo24Hour(row.end_time);
							if (!converted) {
								temp[i][3] = 'Invalid end time format (use h:mm AM/PM, e.g., 9:30 AM)';
							} else {
								// store the converted time
								transformedRows[i].end_time = converted;
							}
						}
					}

					setIsValidating(false);
					setErrList(temp);
				} catch (error) {
					dispatch(
						Actions.showMessage({
							message: 'Error processing file: ' + error.message,
							variant: 'error',
							autoHideDuration: 3000,
						})
					);
				}
			};

			reader.onerror = (error) => {
				dispatch(
					Actions.showMessage({
						message: 'Error reading file: ' + error.message,
						variant: 'error',
						autoHideDuration: 3000,
					})
				);
			};

			reader.readAsBinaryString(file);
		}
	}, []);

	useEffect(() => {
		if (isValidating) {
			return;
		}
		if (errList.flat().length) {
			setErrs(true);
		} else {
			setErrs(false);
		}
	}, [errList]);

	const uploadData = () => {
		const allrows = rows.map((row) => ({
			room_id: row.room_id,
			start_time: row.start_time,
			end_time: row.end_time,
			subject: row.subject,
			type: row.activity_type,
			thumb: undefined,
		}));

		if (!allrows.length) {
			setOpen(false);
			dispatch(Actions.closeDialog());
			dispatch(
				Actions.showMessage({
					message: 'You uploaded an empty file',
					autoHideDuration: 1500,
					variant: 'error',
				})
			);
			return;
		}

		const data = allrows.filter((_, i) => !errList[i]?.length);

		if (data.length === 0) {
			setOpen(false);
			dispatch(Actions.closeDialog());
			dispatch(
				Actions.showMessage({
					message: 'There is no valid data available to upload',
					autoHideDuration: 1500,
					variant: 'error',
				})
			);
			return;
		}

		uploadSchedule({ data })
			.then(() => {
				setOpen(false);
				dispatch(Actions.closeDialog());
				dispatch(
					Actions.showMessage({
						message: 'Schedules uploaded successfully',
						variant: 'success',
					})
				);
			})
			.catch((err) => {
				setOpen(false);
				setIsAdding(false);

				if (err.response?.data) {
					// copy existing errors
					const newErrList = errList.map((row) => [...row]);

					if (err.response.data.errors) {
						// group errors by row index
						const errorsByRow = {};

						Object.entries(err.response.data.errors).forEach(([key, messages]) => {
							const [, rowIndex] = key.split('.');
							if (!errorsByRow[rowIndex]) {
								errorsByRow[rowIndex] = new Set();
							}
							messages.forEach((message) => errorsByRow[rowIndex].add(message));
						});

						// add unique api errors to the error list (don't display start_time and end_time if they are the same)
						Object.entries(errorsByRow).forEach(([rowIndex, messages]) => {
							// preserve any existing validation errors
							if (!newErrList[rowIndex]) {
								newErrList[rowIndex] = [];
							}
							newErrList[rowIndex][3] = Array.from(messages).join('; ');
						});
					}

					setErrList(newErrList);
					setErrs(true);

					dispatch(
						Actions.showMessage({
							message: 'Schedule conflicts detected. Check the errors in red.',
							variant: 'error',
							autoHideDuration: 4000,
						})
					);
				} else {
					dispatch(
						Actions.showMessage({
							message: 'Failed to upload schedules',
							variant: 'error',
						})
					);
				}
			});
	};

	return (
		<div className="p-32 w-[90vw]">
			{!isValidating ? (
				<>
					<div className="flex justify-end">
						<IconButton onClick={() => dispatch(Actions.closeDialog())}>
							<CloseIcon />
						</IconButton>
					</div>
					<TableContainer component={Paper} id="Scrollable-table" style={{ height: '60vh' }}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell className="bg-white studentTableHeader">#</TableCell>
									<TableCell className="bg-white studentTableHeader">Room</TableCell>
									<TableCell className="bg-white studentTableHeader">Start Time</TableCell>
									<TableCell className="bg-white studentTableHeader">End Time</TableCell>
									<TableCell className="bg-white studentTableHeader">Subject</TableCell>
									<TableCell className="bg-white studentTableHeader">Activity Type</TableCell>
									<TableCell className="bg-white studentTableHeader">Errors</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map((row, i) => (
									<TableRow className={`${errList[i]?.some((err) => err) ? 'bg-red-100' : ''}`} key={i}>
										<TableCell>{i + 1}</TableCell>
										<TableCell className={errList[i]?.[1] ? 'bg-red-200' : ''} title={errList[i]?.[1]}>
											{row.room}
										</TableCell>
										<TableCell className={errList[i]?.[2] ? 'bg-red-200' : ''} title={errList[i]?.[2]}>
											{formatTimeForDisplay(row.start_time)}
										</TableCell>
										<TableCell className={errList[i]?.[3] ? 'bg-red-200' : ''} title={errList[i]?.[3]}>
											{formatTimeForDisplay(row.end_time)}
										</TableCell>
										<TableCell>{row.subject}</TableCell>
										<TableCell>{row.activity_type}</TableCell>
										<TableCell className="text-red-600">
											{errList[i]?.some((err) => err) ? (
												<ul className="list-disc">
													{errList[i].map((err, ind) => (err ? <li key={ind}>{err}</li> : null)).filter(Boolean)}
												</ul>
											) : null}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<div className="flex justify-end mx-auto mt-10">
						{errs ? (
							<CustomButton onClick={handleProceedAnyway} variant="primary" width={140}>
								Proceed Anyway
							</CustomButton>
						) : (
							<CustomButton onClick={() => setOpen(true)} variant="primary" width={140}>
								Upload Schedules
							</CustomButton>
						)}
					</div>
				</>
			) : (
				<div className="flex justify-center items-center">
					<CircularProgress size={35} />
				</div>
			)}
			<Dialog
				aria-describedby="alert-dialog-description"
				aria-labelledby="alert-dialog-title"
				onClose={() => setOpen(false)}
				open={open}
			>
				<div className="p-32">
					<div className="flex flex-col gap-36">
						<div className="">
							<Error className="text-red-600 size-[32px]" />
						</div>
						<p className="font-bold">Only valid data will be added to the system. Proceed?</p>
						{isAdding ? (
							<CircularProgress size={35} />
						) : (
							<div className="flex gap-16 justify-end">
								<GradientButton onClick={() => setOpen(false)} size="small" variant="secondary">
									No
								</GradientButton>
								<GradientButton
									onClick={() => {
										setIsAdding(true);
										uploadData();
									}}
									size="small"
									variant="blurple"
								>
									Yes
								</GradientButton>
							</div>
						)}
					</div>
				</div>
			</Dialog>
		</div>
	);
};

export default ScheduleFileValidator;
